import { TabsContent } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChartBar,
  ChartLine,
  Database,
  Download,
  FlowArrow,
  Lightbulb,
  Sparkle,
  Users,
  X,
} from '@phosphor-icons/react';
import { HelpHints, QuickTip } from './HelpHints';
import { ResearchDomainTemplates } from './ResearchDomainTemplates';
import { DatasetCard } from './DatasetCard';
import { DatasetFilters } from './DatasetFilters';
import { APISourceCard } from './APISourceCard';
import { SkeletonLoader } from './SkeletonLoader';
import { DatasetBrowser } from './DatasetBrowser';
import { QualityReport } from './QualityReport';
import { CorrelationDisplay } from './CorrelationDisplay';
import { DataTransformationPipeline } from './DataTransformationPipeline';
import { DataVisualization } from './DataVisualization';
import { DataExport } from './DataExport';
import { APISource, Dataset, ResearchDomainTemplate } from '@/lib/types';
import { mockAPISources, researchDomainTemplates } from '@/lib/mockData';
import { toast } from 'sonner';

// Helper component for empty states to reduce cognitive complexity
interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
}: EmptyStateProps) {
  return (
    <div className='text-center py-12'>
      {icon}
      <h3 className='text-xl font-semibold mb-2'>{title}</h3>
      <p className='text-muted-foreground mb-4'>{description}</p>
      {(primaryAction || secondaryAction) && (
        <div className='flex justify-center gap-2'>
          {primaryAction && (
            <Button onClick={primaryAction.onClick} variant='default'>
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button onClick={secondaryAction.onClick} variant='outline'>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Helper to get correlation action based on dataset count
function getCorrelationAction(
  selectedDatasets: Dataset[],
  onTabChange: (tab: string) => void,
  handleAnalyzeCorrelations: () => void
) {
  if (selectedDatasets.length >= 2) {
    return {
      label: 'Analyze Correlations',
      onClick: handleAnalyzeCorrelations,
    };
  }

  return {
    label: 'Browse Templates',
    onClick: () => onTabChange('templates'),
  };
}

// Helper to get correlation secondary action
function getCorrelationSecondaryAction(
  selectedDatasets: Dataset[],
  onTabChange: (tab: string) => void
) {
  if (selectedDatasets.length < 2) {
    return {
      label: 'Find More Datasets',
      onClick: () => onTabChange('discover'),
    };
  }

  return undefined;
}

// Helper component for common help hints
function TabHelpHints({
  currentTab,
  selectedDatasets,
  qualityReports,
  correlations,
}: {
  currentTab: string;
  selectedDatasets: Dataset[];
  qualityReports: Record<string, any>;
  correlations: any[];
}) {
  return (
    <HelpHints
      currentTab={currentTab}
      selectedDatasetCount={selectedDatasets.length}
      hasQualityReports={Object.keys(qualityReports).length > 0}
      hasCorrelations={correlations.length > 0}
    />
  );
}

interface AppTabsContentProps {
  selectedDatasets: Dataset[];
  activeTab: string;
  selectedSource: APISource | null;
  qualityReports: Record<string, any>;
  correlations: any[];
  filteredSources: APISource[];
  appliedTemplate: ResearchDomainTemplate | null;
  allDatasets: Dataset[];
  totalDatasetCount: number;
  isLoading: boolean;
  onTabChange: (tab: string) => void;
  handleTemplateSelect: (template: ResearchDomainTemplate) => void;
  handleDatasetSelect: (dataset: Dataset) => void;
  handleDatasetPreview: (dataset: Dataset) => void;
  handleSourceSelect: (source: APISource) => void;
  handleFilterChange: (filtered: APISource[]) => void;
  handleAnalyzeCorrelations: () => void;
  handleClearTemplate: () => void;
  handleDataExport: (options: any) => Promise<any>;
  setSelectedDatasets: (datasets: Dataset[]) => void;
  setFilteredSources: (sources: APISource[]) => void;
  setSelectedSource: (source: APISource | null) => void;
}

export function AppTabsContent({
  selectedDatasets,
  activeTab,
  selectedSource,
  qualityReports,
  correlations,
  filteredSources,
  appliedTemplate,
  allDatasets,
  totalDatasetCount,
  isLoading,
  onTabChange,
  handleTemplateSelect,
  handleDatasetSelect,
  handleDatasetPreview,
  handleSourceSelect,
  handleFilterChange,
  handleAnalyzeCorrelations,
  handleClearTemplate,
  handleDataExport,
  setSelectedDatasets,
  setFilteredSources,
  setSelectedSource,
}: AppTabsContentProps) {
  return (
    <>
      {/* Research Templates & Discovery Tab */}
      <TabsContent value='templates' className='space-y-6'>
        <TabHelpHints
          currentTab='templates'
          selectedDatasets={selectedDatasets}
          qualityReports={qualityReports}
          correlations={correlations}
        />

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {/* Templates Section */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Sparkle className='w-5 h-5' />
                Research Domain Templates
              </CardTitle>
              <CardDescription>
                Quick-start templates for common research domains with
                pre-configured filters and recommended datasets
                {appliedTemplate && (
                  <span className='block mt-2 text-sm font-medium text-primary'>
                    Currently applied: {appliedTemplate.name}
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResearchDomainTemplates
                templates={researchDomainTemplates}
                onTemplateSelect={handleTemplateSelect}
              />
            </CardContent>
          </Card>

          {/* Quick Discovery Section */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Lightbulb className='w-5 h-5' />
                Browse Data Sources
              </CardTitle>
              <CardDescription>
                Or start exploring {mockAPISources.length} data sources directly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => onTabChange('discover')}
                className='w-full gap-2'
                variant='outline'
              >
                <Database className='w-4 h-4' />
                Browse All Data Sources
              </Button>

              <div className='mt-4 space-y-2'>
                <div className='text-sm font-medium'>Popular Categories:</div>
                <div className='flex flex-wrap gap-2'>
                  {['Government', 'Entertainment', 'Finance', 'Science'].map(
                    category => (
                      <Button
                        key={category}
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          // Filter by category and go to discover
                          const filtered = mockAPISources.filter(
                            source => source.category === category
                          );

                          setFilteredSources(filtered);
                          onTabChange('discover');
                        }}
                        className='text-xs'
                      >
                        {category}
                      </Button>
                    )
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* Datasets Tab - Shows datasets from selected source */}
      <TabsContent value='datasets' className='space-y-6'>
        {selectedSource ? (
          <>
            <TabHelpHints
              currentTab='datasets'
              selectedDatasets={selectedDatasets}
              qualityReports={qualityReports}
              correlations={correlations}
            />

            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-3'>
                    <div className='p-2 rounded-lg bg-primary text-primary-foreground'>
                      <Database className='w-5 h-5' />
                    </div>
                    <div>
                      <CardTitle className='flex items-center gap-2'>
                        {selectedSource.name} Datasets
                      </CardTitle>
                      <CardDescription>
                        Explore {selectedSource.datasets.length} datasets from{' '}
                        {selectedSource.organization || selectedSource.name}
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setSelectedSource(null);
                      onTabChange('discover');
                    }}
                    className='gap-1'
                  >
                    <X className='w-3 h-3' />
                    Back to Discovery
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                  {selectedSource.datasets.map(dataset => (
                    <DatasetCard
                      key={dataset.id}
                      dataset={dataset}
                      isSelected={selectedDatasets.some(
                        d => d.id === dataset.id
                      )}
                      onSelect={handleDatasetSelect}
                      onPreview={handleDatasetPreview}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <EmptyState
            icon={
              <Database className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
            }
            title='No Data Source Selected'
            description="Click 'Explore' on any data source to view its available datasets"
            primaryAction={{
              label: 'Browse Data Sources',
              onClick: () => onTabChange('discover'),
            }}
          />
        )}
      </TabsContent>

      {/* API Discovery Tab */}
      <TabsContent value='discover' className='space-y-6'>
        <TabHelpHints
          currentTab='discover'
          selectedDatasets={selectedDatasets}
          qualityReports={qualityReports}
          correlations={correlations}
        />

        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-2xl font-bold'>Data Sources</h2>
            <p className='text-muted-foreground'>
              Browse {filteredSources.length} data sources with{' '}
              {totalDatasetCount} datasets
            </p>
          </div>
          <Button
            onClick={() => onTabChange('templates')}
            variant='outline'
            className='gap-2'
          >
            <Sparkle className='w-4 h-4' />
            Back to Templates
          </Button>
        </div>

        {selectedDatasets.length >= 2 && (
          <QuickTip
            type='correlation'
            onAction={() => {
              handleAnalyzeCorrelations();
            }}
          />
        )}

        {appliedTemplate && (
          <Card className='border-accent/20 bg-accent/5'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div>
                  <CardTitle className='flex items-center gap-2 text-accent'>
                    <Sparkle className='w-5 h-5' />
                    {appliedTemplate.name} Template Applied
                  </CardTitle>
                  <CardDescription>
                    {appliedTemplate.description}
                    <div className='flex flex-wrap gap-2 mt-2'>
                      {appliedTemplate.keywords.slice(0, 5).map(keyword => (
                        <Badge
                          key={keyword}
                          variant='secondary'
                          className='text-xs'
                        >
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </CardDescription>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleClearTemplate}
                  className='gap-1'
                >
                  <X className='w-3 h-3' />
                  Clear
                </Button>
              </div>
            </CardHeader>
          </Card>
        )}

        <DatasetFilters
          sources={mockAPISources}
          onFilterChange={handleFilterChange}
          selectedDatasets={selectedDatasets}
        />

        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Users className='w-5 h-5' />
              Public Data Sources
            </CardTitle>
            <CardDescription>
              Explore {filteredSources.length} curated APIs with{' '}
              {totalDatasetCount} datasets for your data science projects
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <SkeletonLoader type='card' count={4} />
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                {filteredSources.map(source => (
                  <APISourceCard
                    key={source.id}
                    source={source}
                    onSelect={handleSourceSelect}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Browse All Datasets Tab */}
      <TabsContent value='browse-all' className='space-y-6'>
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Database className='w-5 h-5' />
              Browse All Datasets
            </CardTitle>
            <CardDescription>
              Explore and filter through all available datasets from all sources
            </CardDescription>
          </CardHeader>
        </Card>

        <DatasetBrowser
          datasets={mockAPISources.flatMap(source => source.datasets)}
          selectedDatasets={selectedDatasets}
          onDatasetSelect={handleDatasetSelect}
          onDatasetPreview={handleDatasetPreview}
        />
      </TabsContent>

      {/* Quality Check Tab */}
      <TabsContent value='quality' className='space-y-6'>
        <TabHelpHints
          currentTab='quality'
          selectedDatasets={selectedDatasets}
          qualityReports={qualityReports}
          correlations={correlations}
        />

        {selectedDatasets.length > 0 ? (
          <div className='space-y-6'>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <ChartLine className='w-5 h-5' />
                  Data Quality Reports
                </CardTitle>
                <CardDescription>
                  Quality analysis for {selectedDatasets.length} selected
                  dataset{selectedDatasets.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
            </Card>

            {selectedDatasets.map(dataset => {
              const report = qualityReports[dataset.id];

              return report ? (
                <QualityReport
                  key={dataset.id}
                  report={report}
                  datasetName={dataset.name}
                />
              ) : null;
            })}
          </div>
        ) : (
          <EmptyState
            icon={
              <ChartLine className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
            }
            title='No Datasets Selected'
            description='Add datasets to view their quality reports and get recommendations for data analysis'
            primaryAction={{
              label: 'Browse Templates',
              onClick: () => onTabChange('templates'),
            }}
            secondaryAction={{
              label: 'Discover Data Sources',
              onClick: () => onTabChange('discover'),
            }}
          />
        )}
      </TabsContent>

      {/* Correlations Tab */}
      <TabsContent value='correlations' className='space-y-6'>
        {correlations.length > 0 ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <ChartLine className='w-5 h-5' />
                  Data Correlations
                </CardTitle>
                <CardDescription>
                  Found {correlations.length} potential correlation
                  {correlations.length !== 1 ? 's' : ''} between your selected
                  datasets
                </CardDescription>
              </CardHeader>
            </Card>
            <CorrelationDisplay correlations={correlations} />
          </>
        ) : (
          <EmptyState
            icon={
              <ChartLine className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
            }
            title='No Correlations Analyzed'
            description="Select at least 2 datasets and click 'Analyze' to find potential correlations"
            primaryAction={getCorrelationAction(
              selectedDatasets,
              onTabChange,
              handleAnalyzeCorrelations
            )}
            secondaryAction={getCorrelationSecondaryAction(
              selectedDatasets,
              onTabChange
            )}
          />
        )}
      </TabsContent>

      {/* Transform Tab */}
      <TabsContent value='transform' className='space-y-6'>
        <TabHelpHints
          currentTab='transform'
          selectedDatasets={selectedDatasets}
          qualityReports={qualityReports}
          correlations={correlations}
        />

        {selectedDatasets.length > 0 ? (
          <DataTransformationPipeline
            datasets={selectedDatasets}
            onPipelineRun={async pipeline => {
              // Handle pipeline execution - could save results or update datasets
              toast.success('Pipeline results saved to workspace');
            }}
            onPipelineSave={async pipeline => {
              // Handle pipeline saving - could store in KV storage
            }}
          />
        ) : (
          <EmptyState
            icon={
              <FlowArrow className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
            }
            title='No Data to Transform'
            description='Select datasets to build interactive transformation pipelines with visual workflow builder'
            primaryAction={{
              label: 'Browse Templates',
              onClick: () => onTabChange('templates'),
            }}
            secondaryAction={{
              label: 'Add Datasets',
              onClick: () => onTabChange('discover'),
            }}
          />
        )}
      </TabsContent>

      {/* Visualize Tab */}
      <TabsContent value='visualize' className='space-y-6'>
        <TabHelpHints
          currentTab='visualize'
          selectedDatasets={selectedDatasets}
          qualityReports={qualityReports}
          correlations={correlations}
        />

        {selectedDatasets.length > 0 ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <ChartBar className='w-5 h-5' />
                  Data Visualization
                </CardTitle>
                <CardDescription>
                  Interactive charts and visualizations for{' '}
                  {selectedDatasets.length} selected dataset
                  {selectedDatasets.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
            </Card>
            <DataVisualization datasets={selectedDatasets} />
          </>
        ) : (
          <EmptyState
            icon={
              <ChartBar className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
            }
            title='No Data to Visualize'
            description='Select datasets to create interactive charts and visualizations'
            primaryAction={{
              label: 'Browse Templates',
              onClick: () => onTabChange('templates'),
            }}
            secondaryAction={{
              label: 'Add Datasets',
              onClick: () => onTabChange('discover'),
            }}
          />
        )}
      </TabsContent>

      {/* Export Tab */}
      <TabsContent value='export' className='space-y-6'>
        <TabHelpHints
          currentTab='export'
          selectedDatasets={selectedDatasets}
          qualityReports={qualityReports}
          correlations={correlations}
        />

        {selectedDatasets.length > 0 ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Download className='w-5 h-5' />
                  Export Data
                </CardTitle>
                <CardDescription>
                  Export {selectedDatasets.length} selected dataset
                  {selectedDatasets.length !== 1 ? 's' : ''} in various formats
                  for use in external tools
                </CardDescription>
              </CardHeader>
            </Card>
            <DataExport
              datasets={selectedDatasets}
              onExport={handleDataExport}
            />
          </>
        ) : (
          <EmptyState
            icon={
              <Download className='w-16 h-16 mx-auto mb-4 text-muted-foreground' />
            }
            title='No Data to Export'
            description='Select datasets to export them in various formats including CSV, JSON, Excel, and more'
            primaryAction={{
              label: 'Browse Templates',
              onClick: () => onTabChange('templates'),
            }}
            secondaryAction={{
              label: 'Add Datasets',
              onClick: () => onTabChange('discover'),
            }}
          />
        )}
      </TabsContent>
    </>
  );
}
